// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

import { useEffect, useMemo, useState } from "react";
import { FinalReviewControls } from "./FinalReviewControls.js";
import { ReviewerProfileEvidence } from "./ReviewerProfileEvidence.js";
import { ReviewerProfileControls } from "./ReviewerProfileControls.js";

import {
  ArrowLeft,
  CircleAlert,
  Code2,
  EyeOff,
  GitCompareArrows,
  History,
  Lightbulb,
  MessageCircleMore,
  PencilLine,
  Play,
  RefreshCw,
  ShieldCheck,
  WandSparkles,
  Wrench,
} from "lucide-react";

import type {
  CockpitApi,
  CockpitReviewWorkspace as Workspace,
  ConversationView,
  FindingEvidence,
  ReviewMaterial,
  ReviewerProfileReference,
  SubjectChange,
} from "./api.js";
import {
  displayState,
  displayReviewRunState,
  pluralize,
  repositoryName,
  reviewDescription,
  reviewReference,
  reviewTitle,
  shortIdentifier,
  stateTone,
} from "./presentation.js";

type QuestionKind =
  "callers" | "draft" | "explain" | "history" | "reproduction";

type InspectorTab = "actions" | "conversation" | "evidence" | "feedback";

const QUESTION_COPY: Readonly<Record<QuestionKind, string>> = {
  callers: "Identify callers and affected behavior for the selected code.",
  draft:
    "Draft concise review feedback for the selected code. Do not publish it.",
  explain:
    "Explain the selected code change, its intent, and its evidence-backed risk.",
  history:
    "Show relevant Chronicle history and prior review evidence for the selected code.",
  reproduction:
    "Propose or run the narrowest authorized reproduction for the selected code.",
};

export function ReviewWorkspace({
  api,
  reviewSeriesId,
}: {
  readonly api: CockpitApi;
  readonly reviewSeriesId: string;
}) {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [material, setMaterial] = useState<ReviewMaterial | null>(null);
  const [selectedLine, setSelectedLine] = useState<number | null>(null);
  const [evidence, setEvidence] = useState<
    Readonly<Record<string, FindingEvidence>>
  >({});
  const [draft, setDraft] = useState("");
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [fixArtifactType, setFixArtifactType] = useState<
    "branch" | "code-suggestion" | "patch" | "regression-test"
  >("code-suggestion");
  const [fixIntent, setFixIntent] = useState("");
  const [fixPaths, setFixPaths] = useState("");
  const [fixPathConversationId, setFixPathConversationId] = useState<
    string | null
  >(null);
  const [status, setStatus] = useState("Loading review workspace…");
  const [busy, setBusy] = useState(false);
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>("evidence");

  async function refresh(announce = true) {
    if (announce) setStatus("Refreshing durable Review projection…");
    try {
      const next = await api.workspace(reviewSeriesId);
      setWorkspace(next);
      const subject =
        next.review.subjects[next.review.series.currentReviewSubjectId];
      const firstPath =
        next.review.inspections[next.review.series.currentReviewRunId ?? ""]
          ?.plan?.changeModel.reviewOrder[0] ?? subject?.changes[0]?.path;
      setSelectedPath((current) => current ?? firstPath ?? null);
      const findings = await Promise.allSettled(
        next.conversations.map(async (conversation) => ({
          evidence: await api.finding(conversation.conversation.findingId),
          findingId: conversation.conversation.findingId,
        })),
      );
      setEvidence(
        Object.fromEntries(
          findings.flatMap((result) =>
            result.status === "fulfilled"
              ? [[result.value.findingId, result.value.evidence] as const]
              : [],
          ),
        ),
      );
      const firstNote = next.conversations[0]?.privateNotes[0];
      if (firstNote !== undefined) setDraft(firstNote.body);
      if (announce) setStatus("");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  }

  useEffect(() => {
    void refresh();
  }, [api, reviewSeriesId]);

  const subject =
    workspace?.review.subjects[
      workspace.review.series.currentReviewSubjectId
    ] ?? null;
  const runId = workspace?.review.series.currentReviewRunId ?? null;
  const inspection =
    runId === null ? null : (workspace?.review.inspections[runId] ?? null);
  const changes = subject?.changes ?? [];
  const orderedChanges = useMemo(() => {
    const order = inspection?.plan?.changeModel.reviewOrder ?? [];
    return [...changes].sort((left, right) => {
      const leftIndex = order.indexOf(left.path);
      const rightIndex = order.indexOf(right.path);
      return (
        (leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex) -
          (rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex) ||
        left.path.localeCompare(right.path)
      );
    });
  }, [changes, inspection]);

  useEffect(() => {
    if (subject === null || selectedPath === null) {
      setMaterial(null);
      return;
    }
    let active = true;
    setSelectedLine(null);
    void api
      .material(subject.reviewSubjectId, selectedPath)
      .then((value) => {
        if (active) setMaterial(value);
      })
      .catch((error: unknown) => {
        if (active) {
          setMaterial(null);
          setStatus(error instanceof Error ? error.message : String(error));
        }
      });
    return () => {
      active = false;
    };
  }, [api, selectedPath, subject]);

  useEffect(() => {
    function keyboard(event: KeyboardEvent) {
      if (
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return;
      }
      const current = orderedChanges.findIndex(
        (change) => change.path === selectedPath,
      );
      if (event.key === "j" && current < orderedChanges.length - 1) {
        event.preventDefault();
        setSelectedPath(orderedChanges[current + 1]?.path ?? null);
      }
      if (event.key === "k" && current > 0) {
        event.preventDefault();
        setSelectedPath(orderedChanges[current - 1]?.path ?? null);
      }
    }
    window.addEventListener("keydown", keyboard);
    return () => {
      window.removeEventListener("keydown", keyboard);
    };
  }, [orderedChanges, selectedPath]);

  const conversation = workspace?.conversations[0] ?? null;
  const findingHeadPaths = useMemo(
    () =>
      [
        ...new Set(
          conversation?.conversation.currentContext.finding.sourceLocations
            .filter((location) => location.side === "head")
            .map((location) => location.path) ?? [],
        ),
      ].sort(),
    [conversation],
  );

  useEffect(() => {
    const conversationId = conversation?.conversation.conversationId ?? null;
    if (conversationId !== fixPathConversationId) {
      setFixPathConversationId(conversationId);
      setFixPaths(findingHeadPaths.join("\n"));
      return;
    }
    setFixPaths((current) => {
      const existing = current
        .split(/[\n,]/u)
        .map((path) => path.trim())
        .filter((path) => path.length > 0);
      return [...new Set([...findingHeadPaths, ...existing])].join("\n");
    });
  }, [conversation, findingHeadPaths, fixPathConversationId]);

  async function performAction(action: Readonly<Record<string, unknown>>) {
    if (conversation === null) {
      setStatus("No grounded Finding Conversation exists for this action.");
      return;
    }
    setBusy(true);
    try {
      const result = await api.reviewAction(
        conversation.conversation.conversationId,
        {
          action,
          actionId: `cockpit-action:${crypto.randomUUID()}`,
          expectedConversationRevision: conversation.conversation.revision,
          source: "cockpit",
        },
      );
      setStatus(
        result.action.state === "accepted" ||
          result.action.state === "completed"
          ? "Action completed through the authoritative Conversation."
          : result.action.state === "partial"
            ? `Action finished partially: ${result.action.outcomeCode}.`
            : `Action did not complete: ${result.action.outcomeCode}.`,
      );
      await refresh(false);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function ask(kind: QuestionKind) {
    const line =
      selectedLine === null
        ? ""
        : ` Focus on displayed diff line ${String(selectedLine + 1)}.`;
    if (kind === "reproduction") {
      if (selectedPath === null) {
        setStatus("Select a changed path before requesting a scoped rerun.");
        return;
      }
      await performAction({
        affectedPaths: [selectedPath],
        kind: "request-scoped-rerun",
        reason: `${QUESTION_COPY[kind]}${line}`,
      });
      return;
    }
    await performAction({
      affectedPaths: selectedPath === null ? [] : [selectedPath],
      body: `${QUESTION_COPY[kind]}${line}`,
      kind: "ask-question",
    });
  }

  async function executeCurrentRun() {
    if (inspection === null || runId === null) {
      setStatus("No registered Review Run is available to start.");
      return;
    }
    setBusy(true);
    setStatus("Starting the review run…");
    try {
      await api.executeReviewRun(runId, inspection.run.revision);
      setStatus("Review execution completed through the authoritative API.");
      await refresh(false);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function startReview(
    requestedReviewerProfile?: ReviewerProfileReference,
  ) {
    if (workspace === null || subject === null) {
      setStatus("The Review Subject is not ready to start.");
      return;
    }
    setBusy(true);
    setStatus("Creating and starting the review run…");
    try {
      await api.startReviewRun({
        ...(requestedReviewerProfile === undefined
          ? {}
          : { requestedReviewerProfile }),
        expectedSeriesRevision: workspace.review.series.revision,
        predecessorReviewRunId: workspace.review.series.currentReviewRunId,
        reviewRunId: `review:${crypto.randomUUID()}`,
        reviewSubjectId: subject.reviewSubjectId,
      });
      setStatus("Review execution completed through the authoritative API.");
      await refresh(false);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      await refresh(false);
    } finally {
      setBusy(false);
    }
  }

  async function requestFix() {
    if (
      conversation === null ||
      subject === null ||
      fixIntent.trim().length === 0
    ) {
      setStatus(
        "Select a grounded Finding and describe the bounded Fix intent.",
      );
      return;
    }
    const repositoryConnectionId = subject.repositoryConnectionIds[0];
    if (repositoryConnectionId === undefined) {
      setStatus("The Review Subject has no authorized Repository Connection.");
      return;
    }
    const repository = workspace?.project.repositories.find(
      (candidate) => candidate.connectionId === repositoryConnectionId,
    );
    if (
      repository?.provider === "git" &&
      fixArtifactType === "code-suggestion"
    ) {
      setStatus(
        "Local Git repositories publish verified branches; choose Patch, Regression test, or Fix branch.",
      );
      return;
    }
    const proposalPaths = [
      ...new Set(
        fixPaths
          .split(/[\n,]/u)
          .map((path) => path.trim())
          .filter((path) => path.length > 0),
      ),
    ];
    if (
      proposalPaths.length === 0 ||
      findingHeadPaths.some((path) => !proposalPaths.includes(path))
    ) {
      setStatus(
        "The explicit proposal path boundary must include every selected Finding path.",
      );
      return;
    }
    const findingId = conversation.conversation.findingId;
    await performAction({
      artifactType: fixArtifactType,
      boundedIntent: fixIntent.trim(),
      kind: "request-fix",
      proposalPaths,
      repositoryConnectionId,
      requiredCheckIds: [
        `reproduction:${findingId}`,
        `affected-test:${findingId}`,
      ],
      targetBranch:
        fixArtifactType === "code-suggestion"
          ? null
          : `diffclaw/fix-${crypto.randomUUID()}`,
      targetHeadRevision: subject.headRevision,
    });
  }

  async function authorizeFix(
    proposal: ConversationView["fixProposals"][number],
  ) {
    const repositoryConnectionId = subject?.repositoryConnectionIds[0];
    const repository = workspace?.project.repositories.find(
      (candidate) => candidate.connectionId === repositoryConnectionId,
    );
    const localGit = repository?.provider === "git";
    const codeSuggestion =
      !localGit && proposal.artifactType === "code-suggestion";
    await performAction({
      adapterId: localGit
        ? "local-git-fix-publication"
        : "github-fix-publication",
      authorizationId: `fix-authorization:${crypto.randomUUID()}`,
      branch: codeSuggestion ? null : proposal.targetBranch,
      expectedProposalRevision: proposal.revision,
      expiresAt: new Date(Date.now() + 10 * 60 * 1_000).toISOString(),
      kind: "authorize-fix-publication",
      proposalId: proposal.proposalId,
      publicationAction: codeSuggestion ? "code-suggestion" : "write-branch",
      reason:
        "Reviewer authorized this exact independently validated proposal for ten minutes.",
    });
  }

  async function publishFix(
    proposal: ConversationView["fixProposals"][number],
  ) {
    const authorization = proposal.publicationAuthorization;
    if (authorization === null) {
      setStatus("This Fix Proposal has no active publication authorization.");
      return;
    }
    await performAction({
      authorizationId: authorization.authorizationId,
      expectedProposalRevision: proposal.revision,
      kind: "publish-fix",
      proposalId: proposal.proposalId,
    });
  }

  async function saveDraft() {
    if (conversation === null || draft.trim().length === 0) {
      setStatus("A grounded Conversation and non-empty draft are required.");
      return;
    }
    setBusy(true);
    try {
      const noteId =
        conversation.privateNotes[0]?.noteId ??
        `private-feedback:${conversation.conversation.conversationId}`;
      await api.savePrivateNote(
        conversation.conversation.conversationId,
        noteId,
        {
          body: draft,
          expectedConversationRevision: conversation.conversation.revision,
          source: "cockpit",
        },
      );
      setStatus("Private draft saved. Nothing was published.");
      await refresh(false);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function exportArtifact(
    findingId: string,
    artifactId: string,
    receiptRevision: number,
  ) {
    setBusy(true);
    setStatus(`Verifying and exporting ${artifactId}…`);
    try {
      const exported = await api.exportEvidenceArtifact(
        findingId,
        artifactId,
        receiptRevision,
      );
      const binary = atob(exported.bodyBase64);
      const bytes = Uint8Array.from(binary, (character) =>
        character.charCodeAt(0),
      );
      const blob = new Blob([bytes], { type: exported.mediaType });
      const objectUrl = URL.createObjectURL(blob);
      const suggestedName =
        /filename="([A-Za-z0-9._:@+-]+)"/u.exec(
          exported.contentDisposition,
        )?.[1] ?? artifactId;
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = suggestedName;
      anchor.hidden = true;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
      setStatus(
        `Exported ${artifactId} (${String(exported.sizeBytes)} bytes, SHA-256 ${exported.sha256.slice(0, 12)}…).`,
      );
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  if (workspace === null) {
    return (
      <div className="page">
        <a className="back-link" href="#/reviews">
          <ArrowLeft size={15} aria-hidden="true" />
          All reviews
        </a>
        <div className="empty-state" role="status">
          <strong>{status}</strong>
          <span>
            The review opens once its durable projection has been read.
          </span>
        </div>
      </div>
    );
  }

  const examined = inspection?.outcome?.coverage.examinedPaths.length ?? 0;
  const total = changes.length;
  const progress = total === 0 ? 0 : Math.round((examined / total) * 100);
  const blindSpots = workspace.blindSpots.length;
  const reference = reviewReference(reviewSeriesId);
  const repository = workspace.project.repositories.find(
    (candidate) =>
      candidate.connectionId === subject?.repositoryConnectionIds[0],
  );
  const runState = inspection?.run.state ?? "not-started";
  const activeRun =
    inspection !== null &&
    runId !== null &&
    !["cancelled", "completed", "failed", "superseded"].includes(runState);

  return (
    <div className="page page-wide">
      <a className="back-link" href="#/reviews">
        <ArrowLeft size={15} aria-hidden="true" />
        All reviews
      </a>

      <header className="workspace-head">
        <div>
          <h1>
            {subject === null
              ? "Loading change intent"
              : reviewTitle(subject.declaredIntent.summary)}
          </h1>
          {subject === null ||
          reviewDescription(subject.declaredIntent.summary) === null ? null : (
            <p className="row-summary">
              {reviewDescription(subject.declaredIntent.summary)}
            </p>
          )}
          <div className="workspace-provenance">
            {repository === undefined ? null : (
              <span className="repo">
                {repositoryName(
                  repository.repositoryUrl,
                  repository.displayName,
                )}
              </span>
            )}
            {reference === null ? null : <span>{reference}</span>}
            <span className="revision-line">
              <GitCompareArrows size={14} aria-hidden="true" />
              <span title={subject?.baseRevision}>
                {shortIdentifier(subject?.baseRevision ?? "", 8)}
              </span>
              <span aria-hidden="true">&rarr;</span>
              <span title={subject?.headRevision}>
                {shortIdentifier(subject?.headRevision ?? "", 8)}
              </span>
            </span>
            <span>
              {changes.length} changed {pluralize(changes.length, "file")}
            </span>
          </div>
        </div>
        <div className="workspace-head-actions">
          <span
            className={`state-badge tone-${stateTone(workspace.providerProjection.state)}`}
          >
            {workspace.providerProjection.state === "current"
              ? "Provider synced"
              : `Provider ${displayState(workspace.providerProjection.state).toLocaleLowerCase()}`}
          </span>
          <span className={`state-badge tone-${stateTone(runState)}`}>
            {displayReviewRunState(runState)}
          </span>
          <button
            className="button secondary"
            onClick={() => void refresh()}
            type="button"
          >
            <RefreshCw size={16} aria-hidden="true" />
            Refresh
          </button>
        </div>
      </header>

      {/*
        One coverage statement. The examined share and the gaps that remain sit
        in the same element so they can never contradict each other the way two
        separate badges did.
      */}
      <div className="coverage-bar">
        <span className="coverage-bar-copy">
          {examined} of {total} {pluralize(total, "file")} examined
          <small>{progress}%</small>
        </span>
        <span
          className="coverage-track"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Share of changed files examined"
        >
          <span
            className={
              progress === 100 && blindSpots === 0
                ? "coverage-fill complete"
                : "coverage-fill"
            }
            style={{ width: `${String(progress)}%` }}
          />
        </span>
        <span
          className={blindSpots > 0 ? "coverage-gaps" : "coverage-gaps none"}
        >
          {blindSpots > 0 ? (
            <CircleAlert size={15} aria-hidden="true" />
          ) : (
            <EyeOff size={15} aria-hidden="true" />
          )}
          {blindSpots} open {pluralize(blindSpots, "blind spot")}
        </span>
      </div>

      {blindSpots === 0 ? null : (
        <details className="callout warning gaps-callout">
          <summary>
            <CircleAlert size={17} aria-hidden="true" />
            {blindSpots} {pluralize(blindSpots, "blind spot")} remain in this
            review
          </summary>
          <ul>
            {workspace.blindSpots.map((blindSpot) => (
              <li key={blindSpot}>{blindSpot}</li>
            ))}
          </ul>
        </details>
      )}

      <details className="disclosure" open={runId === null}>
        <summary>Configure the next review</summary>
        <ReviewerProfileControls
          key={reviewSeriesId}
          api={api}
          busy={busy}
          canStart={!activeRun && subject !== null}
          hasRun={runId !== null}
          onStart={startReview}
        />
      </details>

      {!activeRun ? null : (
        <section className="run-controls" aria-label="Review run controls">
          <span className="row-signal">
            <strong>{displayReviewRunState(runState)}</strong> &middot; revision{" "}
            {inspection.run.revision}
          </span>
          {runState === "registered" ? (
            <button
              className="button primary"
              disabled={busy}
              onClick={() => void executeCurrentRun()}
              type="button"
            >
              <Play size={15} aria-hidden="true" />
              Start review
            </button>
          ) : null}
          {runState === "partial" ? (
            <button
              className="button primary"
              disabled={busy || conversation === null}
              onClick={() =>
                void performAction({
                  expectedReviewRunRevision: inspection.run.revision,
                  kind: "resume-review-run",
                  reviewRunId: runId,
                })
              }
              type="button"
            >
              Resume review
            </button>
          ) : null}
          <button
            className="button danger-quiet"
            disabled={busy || conversation === null}
            onClick={() =>
              void performAction({
                expectedReviewRunRevision: inspection.run.revision,
                kind: "cancel-review-run",
                reason: "Reviewer cancelled this Review Run in the cockpit.",
                reviewRunId: runId,
              })
            }
            type="button"
          >
            Cancel review
          </button>
        </section>
      )}

      <div className="review-layout">
        <div className="review-main">
          <aside className="file-rail" aria-label="Changed files">
            <div className="file-rail-heading">
              <span>Changed files</span>
              <span className="count">most risk first</span>
            </div>
            <div className="file-list">
              {orderedChanges.map((change) => (
                <FileButton
                  change={change}
                  key={change.path}
                  selected={change.path === selectedPath}
                  onSelect={() => {
                    setSelectedPath(change.path);
                  }}
                  risks={inspection?.plan?.changeModel.riskMap ?? []}
                />
              ))}
            </div>
            <p className="keyboard-hint">
              <kbd>j</kbd> <kbd>k</kbd> move between files
            </p>
          </aside>

          <section className="diff-pane" aria-labelledby="diff-title">
            <div className="pane-heading">
              <h2 id="diff-title">{selectedPath ?? "Select a file"}</h2>
              <span className="hint">
                {material === null
                  ? "Loading source"
                  : material.change.material.availability === "available"
                    ? "Exact source at this revision"
                    : "Source unavailable"}
              </span>
            </div>
            <DiffViewer
              material={material}
              selectedLine={selectedLine}
              onSelectLine={setSelectedLine}
            />
          </section>

          <section className="panel" aria-labelledby="selected-actions-title">
            <div className="panel-head">
              <div>
                <h3 id="selected-actions-title">Ask about the selected code</h3>
                <p>
                  Every question carries this review&rsquo;s exact revision and
                  path, so answers stay attached to the code you are looking at.
                </p>
              </div>
            </div>
            <div className="panel-pad">
              <div className="selected-actions">
                <button
                  className="button secondary"
                  disabled={
                    busy || conversation === null || selectedPath === null
                  }
                  onClick={() =>
                    selectedPath === null
                      ? undefined
                      : void performAction({
                          affectedPaths: [selectedPath],
                          kind: "request-scoped-rerun",
                          reason:
                            "Reviewer requested affected work for the selected path.",
                        })
                  }
                  type="button"
                >
                  <RefreshCw size={15} aria-hidden="true" />
                  Rerun selected path
                </button>
                {(
                  [
                    ["explain", "Explain", Lightbulb],
                    ["callers", "Find callers", Code2],
                    ["history", "Show history", History],
                    ["reproduction", "Reproduce", Play],
                    ["draft", "Draft feedback", PencilLine],
                  ] as const
                ).map(([kind, label, Icon]) => (
                  <button
                    className="button tertiary"
                    disabled={busy || conversation === null}
                    key={kind}
                    onClick={() => void ask(kind)}
                    type="button"
                  >
                    <Icon size={15} aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>
              {conversation === null ? (
                <p className="hint" style={{ marginTop: "12px" }}>
                  These become available once an evidence-backed finding opens a
                  grounded conversation.
                </p>
              ) : null}
            </div>
          </section>
        </div>

        <aside
          className="inspector"
          aria-label="Review evidence and conversation"
        >
          <div
            className="inspector-tabs"
            role="tablist"
            aria-label="Review inspector"
          >
            {(
              [
                ["evidence", "Evidence", ShieldCheck],
                ["conversation", "Thread", MessageCircleMore],
                ["feedback", "Feedback", PencilLine],
                ["actions", "Fixes", Wrench],
              ] as const
            ).map(([tab, label, Icon]) => (
              <button
                aria-selected={inspectorTab === tab}
                key={tab}
                onClick={() => {
                  setInspectorTab(tab);
                }}
                role="tab"
                type="button"
              >
                <Icon
                  size={16}
                  strokeWidth={inspectorTab === tab ? 2.2 : 1.7}
                  aria-hidden="true"
                />
                {label}
              </button>
            ))}
          </div>
          <div className="inspector-content">
            {inspectorTab === "evidence" ? (
              <EvidenceSummary
                busy={busy}
                inspection={inspection}
                evidence={evidence}
                onExport={exportArtifact}
              />
            ) : null}
            {inspectorTab === "conversation" ? (
              <ConversationSummary workspace={workspace} />
            ) : null}
            {inspectorTab === "actions" ? (
              <section className="fix-panel" aria-labelledby="fix-panel-title">
                <div>
                  <h3 id="fix-panel-title">Build a bounded fix</h3>
                  <p>
                    Generation runs without source-write credentials. Validation
                    uses a separate identity and records every failed or skipped
                    check.
                  </p>
                </div>
                <label htmlFor="fix-artifact-type">
                  Requested artifact
                  <select
                    id="fix-artifact-type"
                    value={fixArtifactType}
                    onChange={(event) => {
                      setFixArtifactType(
                        event.target.value as typeof fixArtifactType,
                      );
                    }}
                  >
                    <option value="code-suggestion">Code2 suggestion</option>
                    <option value="patch">Patch</option>
                    <option value="regression-test">Regression test</option>
                    <option value="branch">Fix branch</option>
                  </select>
                </label>
                <label htmlFor="fix-intent">
                  Bounded intent
                  <textarea
                    id="fix-intent"
                    rows={3}
                    value={fixIntent}
                    onChange={(event) => {
                      setFixIntent(event.target.value);
                    }}
                    placeholder="Describe only the behavior this finding should change."
                  />
                </label>
                <label htmlFor="fix-paths">
                  Paths the fix may touch
                  <textarea
                    className="code-input"
                    id="fix-paths"
                    rows={3}
                    value={fixPaths}
                    onChange={(event) => {
                      setFixPaths(event.target.value);
                    }}
                    placeholder="One repository-relative path per line."
                  />
                  <span className="field-help">
                    Add the new test path when requesting a regression test.
                  </span>
                </label>
                <button
                  className="button secondary"
                  disabled={
                    busy ||
                    conversation === null ||
                    subject === null ||
                    fixIntent.trim().length === 0
                  }
                  onClick={() => void requestFix()}
                  type="button"
                >
                  <WandSparkles size={15} aria-hidden="true" />
                  Generate and independently validate
                </button>
                {conversation?.fixProposals.map((proposal) => (
                  <article className="finding-card" key={proposal.proposalId}>
                    <div className="coaching-memory-head">
                      <strong>{displayState(proposal.artifactType)}</strong>
                      <span
                        className={`state-badge tone-${stateTone(proposal.state)}`}
                      >
                        {displayState(proposal.state)}
                      </span>
                    </div>
                    <p>{proposal.boundedIntent}</p>
                    <p className="hint">
                      Bounded to {proposal.proposalPaths.join(", ")}
                    </p>
                    {proposal.generation === null ? null : (
                      <>
                        <p>{proposal.generation.explanation}</p>
                        <p className="hint">
                          Behavior delta: {proposal.generation.behaviorDelta}
                        </p>
                      </>
                    )}
                    {proposal.validation === null ||
                    proposal.validation.checks.length === 0 ? null : (
                      <ul className="artifact-list">
                        {proposal.validation.checks.map((check) => (
                          <li key={check.checkId}>
                            {check.kind}: {check.state} ({check.reasonCode})
                          </li>
                        ))}
                      </ul>
                    )}
                    {proposal.state === "verified" ? (
                      <button
                        className="button danger-quiet"
                        disabled={busy}
                        onClick={() => void authorizeFix(proposal)}
                        type="button"
                      >
                        Authorize provider write for 10 minutes
                      </button>
                    ) : null}
                    {proposal.state === "publication-authorized" ? (
                      <button
                        className="button danger"
                        disabled={busy}
                        onClick={() => void publishFix(proposal)}
                        type="button"
                      >
                        Publish authorized proposal
                      </button>
                    ) : null}
                    {proposal.publicationReference === null ? null : (
                      <p className="hint">
                        Published as {proposal.publicationReference}
                      </p>
                    )}
                  </article>
                ))}
              </section>
            ) : null}
            {inspectorTab === "feedback" ? (
              <>
                <FinalReviewControls
                  key={reviewSeriesId}
                  api={api}
                  conversations={workspace.conversations}
                  busy={busy}
                  onBusy={setBusy}
                  onRefresh={() => refresh(false)}
                />
                {/*
                  The private/published boundary is the most consequential
                  affordance here, so it is drawn as an enclosure rather than
                  implied by a caption.
                */}
                <section className="draft-panel">
                  <p className="privacy-banner">
                    <EyeOff size={13} aria-hidden="true" />
                    Private to you
                  </p>
                  <label htmlFor="feedback-draft">
                    Editable reviewer feedback
                    <textarea
                      id="feedback-draft"
                      rows={8}
                      value={draft}
                      onChange={(event) => {
                        setDraft(event.target.value);
                        setConfirmPublish(false);
                      }}
                      placeholder="Nothing here is visible to anyone else until you publish it."
                    />
                  </label>
                  <div className="button-row">
                    <button
                      className="button secondary"
                      disabled={
                        busy ||
                        conversation === null ||
                        draft.trim().length === 0
                      }
                      onClick={() => void saveDraft()}
                      type="button"
                    >
                      Save private draft
                    </button>
                    {!confirmPublish ? (
                      <button
                        className="button danger-quiet"
                        disabled={
                          busy ||
                          conversation === null ||
                          draft.trim().length === 0
                        }
                        onClick={() => {
                          setConfirmPublish(true);
                        }}
                        type="button"
                      >
                        Prepare publication
                      </button>
                    ) : (
                      <button
                        className="button danger"
                        disabled={busy}
                        onClick={() => {
                          setConfirmPublish(false);
                          void performAction({
                            body: draft,
                            kind: "publish-final-feedback",
                          });
                        }}
                        type="button"
                      >
                        Confirm publish to provider
                      </button>
                    )}
                  </div>
                  {confirmPublish ? (
                    <p className="hint">
                      This sends the draft to the provider thread. It cannot be
                      unsent.
                    </p>
                  ) : null}
                </section>
                {conversation === null ? null : (
                  <section className="disposition-panel">
                    <h3>Record a disposition</h3>
                    <div className="button-row">
                      {(["fixed", "accepted-risk", "rejected"] as const).map(
                        (disposition) => (
                          <button
                            className="button secondary small"
                            disabled={busy}
                            key={disposition}
                            onClick={() =>
                              void performAction({
                                disposition,
                                expectedFindingRevision:
                                  conversation.conversation.currentContext
                                    .finding.findingRevision,
                                kind: "propose-finding-disposition",
                                reason: `Reviewer proposed ${disposition} in the cockpit.`,
                                reviewSubjectId:
                                  conversation.conversation.currentContext
                                    .review.reviewSubjectId,
                              })
                            }
                            type="button"
                          >
                            {displayState(disposition)}
                          </button>
                        ),
                      )}
                    </div>
                  </section>
                )}
              </>
            ) : null}
          </div>
        </aside>
      </div>
      <p className="status-line" aria-live="polite">
        {status}
      </p>
    </div>
  );
}

function FileButton({
  change,
  onSelect,
  risks,
  selected,
}: {
  readonly change: SubjectChange;
  readonly onSelect: () => void;
  readonly risks: readonly {
    readonly level: "high" | "low" | "medium";
    readonly paths: readonly string[];
  }[];
  readonly selected: boolean;
}) {
  const risk =
    risks.find((candidate) => candidate.paths.includes(change.path))?.level ??
    "low";
  const directory = change.path.split("/").slice(0, -1).join("/");
  return (
    <button
      aria-current={selected}
      className="file-item"
      onClick={onSelect}
      type="button"
    >
      <span
        className={risk === "low" ? "file-flag" : "file-flag attention"}
        aria-label={`${risk} risk`}
      />
      <strong>{change.path.split("/").at(-1)}</strong>
      <span className="change-count">
        <span className="addition">+{change.additions}</span>
        <span className="deletion">&minus;{change.deletions}</span>
      </span>
      {directory === "" ? null : <small>{directory}/</small>}
    </button>
  );
}

function DiffViewer({
  material,
  onSelectLine,
  selectedLine,
}: {
  readonly material: ReviewMaterial | null;
  readonly onSelectLine: (line: number) => void;
  readonly selectedLine: number | null;
}) {
  if (material === null) {
    return <div className="diff-empty">Loading the exact source…</div>;
  }
  if (material.material.availability === "unavailable") {
    return (
      <div className="diff-empty">
        <CircleAlert size={18} aria-hidden="true" />
        <br />
        This file&rsquo;s source is unavailable: {material.material.reason}
      </div>
    );
  }
  return (
    <div
      className="diff-view"
      role="listbox"
      aria-label="Exact source for the selected file"
    >
      {material.material.body.split("\n").map((line, index) => {
        // File headers begin with --- and +++ but are not deleted or added
        // lines; colouring them as such misreports the size of the change.
        const kind =
          line.startsWith("diff ") ||
          line.startsWith("index ") ||
          line.startsWith("--- ") ||
          line.startsWith("+++ ")
            ? "meta"
            : line.startsWith("@@")
              ? "hunk"
              : line.startsWith("+")
                ? "addition"
                : line.startsWith("-")
                  ? "deletion"
                  : "context";
        return (
          <button
            aria-current={selectedLine === index}
            aria-selected={selectedLine === index}
            className={`diff-line ${kind}`}
            key={`${String(index)}:${line}`}
            onClick={() => {
              onSelectLine(index);
            }}
            role="option"
            type="button"
          >
            <span className="ln" aria-hidden="true">
              {index + 1}
            </span>
            <code className="code">{line || " "}</code>
          </button>
        );
      })}
    </div>
  );
}

function EvidenceSummary({
  busy,
  evidence,
  inspection,
  onExport,
}: {
  readonly busy: boolean;
  readonly evidence: Readonly<Record<string, FindingEvidence>>;
  readonly inspection: Workspace["review"]["inspections"][string] | null;
  readonly onExport: (
    findingId: string,
    artifactId: string,
    receiptRevision: number,
  ) => Promise<void>;
}) {
  const findings = Object.values(evidence);
  const learning = inspection?.plan?.chronicleSnapshot.learningContext;
  const usage = inspection?.outcome?.usage;
  return (
    <section className="evidence-section">
      {usage?.unsettledUsd === undefined ? null : (
        <dl className="fact-list" aria-label="Review cost">
          <dt>Measured spend</dt>
          <dd>${usage.measuredUsd?.toFixed(6)}</dd>
          <dt>Unsettled reservation</dt>
          <dd>${usage.unsettledUsd.toFixed(6)}</dd>
          <dt>Recorded upper bound</dt>
          <dd>${usage.estimatedUsd.toFixed(6)}</dd>
        </dl>
      )}
      {inspection === null ? null : (
        <ReviewerProfileEvidence inspection={inspection} />
      )}
      {inspection?.plan === null || inspection === null ? (
        <p className="muted">
          No analysis plan exists yet, so there is nothing to stand on for this
          change.
        </p>
      ) : (
        <>
          <div>
            <h3>What the analysis understood</h3>
            <p>{inspection.plan.changeNarrative}</p>
          </div>
          <details className="disclosure">
            <summary>How this change was grouped</summary>
            <ul className="fact-list">
              {inspection.plan.changeModel.logicalGroups.map((group) => (
                <li className="finding-card" key={group.groupId}>
                  <strong>{group.rationale}</strong>
                  <p className="hint">{group.paths.join(", ")}</p>
                </li>
              ))}
            </ul>
          </details>
          <details className="disclosure">
            <summary>Where the risk sits</summary>
            <ul className="fact-list">
              {inspection.plan.changeModel.riskMap.map((risk) => (
                <li className="finding-card" key={risk.riskId}>
                  <strong>{displayState(risk.level)} risk</strong>
                  <p>{risk.reasons.join(", ")}</p>
                </li>
              ))}
            </ul>
          </details>
          <details className="disclosure">
            <summary>
              Repository memory used
              {learning === undefined
                ? ""
                : ` · ${String(learning.activeLearning.length)} active learnings, ${String(learning.relatedReviews.length)} related reviews`}
            </summary>
            {learning === undefined ? null : (
              <div className="stack">
                <p className="hint">
                  Learning is {displayState(learning.activeLearningState)};
                  semantic retrieval is {displayState(learning.retrieval.state)}
                  {learning.retrieval.reasonCode === null
                    ? "."
                    : ` (${displayState(learning.retrieval.reasonCode)}).`}
                </p>
                {learning.activeLearning.length === 0 ? (
                  <p className="muted">No active project learning was used.</p>
                ) : (
                  <ul className="fact-list">
                    {learning.activeLearning.map((item) => (
                      <li className="finding-card" key={item.proposalId}>
                        <strong>{item.summary}</strong>
                        <p className="hint">
                          {displayState(item.outcome)} · {item.proposalId} ·
                          Evaluation {item.evaluationPromotionId}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
                {learning.relatedReviews.length === 0 ? null : (
                  <ul className="fact-list">
                    {learning.relatedReviews.map((item) => (
                      <li className="finding-card" key={item.episodeId}>
                        <strong>{item.rationale}</strong>
                        <p className="hint">
                          {item.sourceObjectId} ·{" "}
                          {displayState(item.disposition)}
                          {item.retrieval === "semantic"
                            ? ` · ${String(Math.round(item.similarity * 100))}% semantic match`
                            : " · lexical fallback"}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
                {learning.activeLearningTruncated ||
                learning.relatedReviewsTruncated ? (
                  <p className="hint">
                    Additional memory was intentionally omitted by the bounded
                    Review Plan.
                  </p>
                ) : null}
              </div>
            )}
          </details>
          <details className="disclosure">
            <summary>Analysis provenance</summary>
            <dl className="fact-list">
              <div className="fact">
                <dt>History</dt>
                <dd>
                  {displayState(
                    inspection.plan.chronicleSnapshot.historicalBackfill,
                  )}
                </dd>
              </div>
              <div className="fact">
                <dt>Knowledge version</dt>
                <dd>{inspection.plan.chronicleSnapshot.knowledgeWatermark}</dd>
              </div>
              <div className="fact">
                <dt>Protocols</dt>
                <dd>
                  {inspection.plan.protocolSelections
                    .map(
                      (protocol) =>
                        `${protocol.protocolId}@${protocol.protocolVersion}`,
                    )
                    .join(", ")}
                </dd>
              </div>
            </dl>
          </details>
        </>
      )}

      {findings.length === 0 ? null : (
        <div>
          <h3>
            {findings.length} {pluralize(findings.length, "finding")}
          </h3>
        </div>
      )}
      {findings.map((item) => (
        <article className="finding-card" key={item.finding.findingId}>
          <div className="coaching-memory-head">
            <span
              className={`state-badge tone-${stateTone(item.finding.severity)}`}
            >
              {displayState(item.finding.severity)} severity
            </span>
            <span className="state-badge">
              {displayState(item.finding.evidenceGrade)}
            </span>
          </div>
          <strong>{item.candidate.claim}</strong>
          <p>{item.candidate.impact}</p>
          {item.receipt.artifactReferences.length === 0 ? null : (
            <ul className="artifact-list" aria-label="Evidence artifacts">
              {item.receipt.artifactReferences.map((artifact) => (
                <li key={artifact.artifactId}>
                  <div>
                    <strong>{artifact.artifactId}</strong>
                    <p className="hint">
                      {artifact.mediaType}, {artifact.sizeBytes} bytes,{" "}
                      {artifact.availability}
                    </p>
                  </div>
                  <button
                    className="button tertiary small"
                    disabled={
                      busy ||
                      artifact.authorization !== "authorized" ||
                      artifact.availability !== "available"
                    }
                    onClick={() =>
                      void onExport(
                        item.finding.findingId,
                        artifact.artifactId,
                        item.receipt.revision,
                      )
                    }
                    type="button"
                  >
                    Download
                  </button>
                </li>
              ))}
            </ul>
          )}
          {item.receipt.verificationAttempts.map((attempt) => (
            <details className="disclosure" key={attempt.attemptId}>
              <summary>
                {attempt.evidenceKind}: proof {attempt.proofOutcome}, disproof{" "}
                {attempt.disproofOutcome}
              </summary>
              <p>{attempt.reasoning}</p>
              {attempt.limitations.length === 0 ? null : (
                <p className="hint">
                  Limitations: {attempt.limitations.join("; ")}
                </p>
              )}
            </details>
          ))}
        </article>
      ))}
    </section>
  );
}

function ConversationSummary({ workspace }: { readonly workspace: Workspace }) {
  return (
    <section className="conversation-section">
      {workspace.conversations.length === 0 ? (
        <p className="muted">
          No conversation has opened yet. One starts when a finding has evidence
          behind it.
        </p>
      ) : (
        workspace.conversations.map((view) => (
          <article key={view.conversation.conversationId}>
            <div className="conversation-meta">
              <span>{displayState(view.conversation.state)}</span>
              <span>
                {view.deliveryIntents.length} publication{" "}
                {pluralize(view.deliveryIntents.length, "intent")}
              </span>
            </div>
            {view.messages.map((message) => (
              <div className="finding-card" key={message.messageId}>
                <strong>{displayState(message.kind)}</strong>
                <p>{message.body}</p>
                <time className="hint" dateTime={message.occurredAt}>
                  {new Date(message.occurredAt).toLocaleString()}
                </time>
              </div>
            ))}
          </article>
        ))
      )}
    </section>
  );
}
